"use client";

import { useEffect, useRef } from "react";
import {
  AdMob,
  BannerAdPosition,
  BannerAdSize,
} from "@capacitor-community/admob";
import { ensureAdMobReady, isNativeIos, nativeBannerConfig } from "../admob";

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

type AdSlotProps = {
  format?: "auto" | "horizontal";
  label?: string;
};

function adsenseConfig(): { client: string; slot?: string } | null {
  const environment = (import.meta as ImportMeta & {
    env?: Record<string, string | undefined>;
  }).env;
  const rawClient = environment?.VITE_ADSENSE_CLIENT?.trim();
  const client = rawClient
    ? rawClient.startsWith("pub-")
      ? `ca-${rawClient}`
      : rawClient
    : undefined;
  const slot = environment?.VITE_ADSENSE_SLOT?.trim();
  return client ? { client, ...(slot ? { slot } : {}) } : null;
}

export function AdSlot({ format = "auto", label = "広告スペース" }: AdSlotProps) {
  const isNativeApp = isNativeIos();
  const config = adsenseConfig();
  const client = config?.client;
  const slot = config?.slot;
  const insRef = useRef<HTMLModElement | null>(null);

  useEffect(() => {
    if (!isNativeApp) return;

    let disposed = false;
    const { adUnitId, isTesting } = nativeBannerConfig();

    const showNativeBanner = async () => {
      try {
        if (!(await ensureAdMobReady()) || disposed) return;

        await AdMob.showBanner({
          adId: adUnitId,
          adSize: BannerAdSize.ADAPTIVE_BANNER,
          isTesting,
          margin: 0,
          position: BannerAdPosition.BOTTOM_CENTER,
        });
      } catch (error) {
        console.warn("AdMob banner could not be shown", error);
      }
    };

    void showNativeBanner();

    return () => {
      disposed = true;
      void AdMob.removeBanner().catch(() => undefined);
    };
  }, [isNativeApp]);

  useEffect(() => {
    if (isNativeApp || !client) return;

    const scriptId = "bwf-adsense-script";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    const pushAd = () => {
      if (!slot) return;
      try {
        window.adsbygoogle = window.adsbygoogle ?? [];
        window.adsbygoogle.push({});
      } catch {
        // Ad blockers and privacy settings are expected to block this call.
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`;
      script.addEventListener("load", pushAd, { once: true });
      document.head.appendChild(script);
    } else {
      pushAd();
    }
  }, [client, isNativeApp, slot]);

  // AdSense is for the web/PWA surface. Native iOS uses the AdMob SDK overlay;
  // do not load AdSense inside the WKWebView.
  if (isNativeApp) return null;

  if (!client) {
    return (
      <aside aria-label={label} className="ad-slot ad-placeholder">
        <span>{label}</span>
        <small>AdSense IDを設定するとここに広告が表示されます</small>
      </aside>
    );
  }

  if (!slot) {
    return <aside aria-label={label} className="ad-slot ad-auto-slot" />;
  }

  return (
    <aside aria-label={label} className="ad-slot">
      <ins
        className="adsbygoogle"
        data-ad-client={client}
        data-ad-format={format}
        data-ad-slot={slot}
        data-full-width-responsive="true"
        ref={insRef}
        style={{ display: "block" }}
      />
    </aside>
  );
}
